import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthSession } from '@domain/auth/useAuthSession';
import type { AppConfig } from '@domain/appConfig/types';
import type { AuthSessionUser, RegistrationPolicy } from '@domain/auth/types';
import { clearCredentials } from '@domain/auth/storage';
import type { UserSearchResult } from '@domain/contacts/types';
import type { ContactDirectoryUser } from '@domain/contacts/types';
import type { ChatFileMeta, ChatMessage, ChatSession, SessionKind } from '@domain/chat/types';
import { buildMessageIdentity } from '@domain/chat/outgoingMessageState.js';
import { downloadChatFile, uploadMedia, type MediaAsset } from '@domain/media/api';
import { chatFileSizeError } from '@shared/media/uploadPolicy';
import type { ImClient, ImUserPresence } from '../../im/imClient';
import { getProto } from '../../im/pb-imports';
import { chatRepository } from '../../db/repository';
import { AuthScreen as MantineAuthScreen } from '../auth/components/AuthScreen';
import { useOAuthLoginOptions } from '../auth/hooks/useOAuthLoginOptions';
import { WorkspaceListPanel } from './components/WorkspaceSidebar';
import { AdaptiveWorkspaceShell } from './components/AdaptiveWorkspaceShell';
import { WorkspaceNavRail } from './components/WorkspaceNavRail';
import { MobileBottomTabBar } from './components/MobileBottomTabBar';
import { getWorkspaceNavigationItems, type ActiveTab } from './components/workspaceNavigationItems';
import { GroupManagementModals } from './dialogs/GroupManagementModals';
import { WorkspaceOverlays } from './dialogs/WorkspaceOverlays';
import { RealtimeCollaborationOverlays } from './dialogs/RealtimeCollaborationOverlays';
import { UserProfileModal } from './dialogs/UserProfileModal';
import { LogoutConfirmModal } from './dialogs/LogoutConfirmModal';
import { ImagePreviewModal } from './dialogs/ImagePreviewModal';
import { ImageLightbox } from './dialogs/ImageLightbox';
import { PlazaStage } from './stages/PlazaStage';
import { HubsStage } from './stages/HubsStage';
import { ConversationStage } from './stages/ConversationStage';
import { SettingsStage } from './stages/SettingsStage';
import { useWorkspaceDirectory } from './hooks/useWorkspaceDirectory';
import { useWorkspaceConfig } from './hooks/useWorkspaceConfig';
import { useWorkspaceCall } from './hooks/useWorkspaceCall';
import { useRealtimeCollaboration } from './hooks/useRealtimeCollaboration';
import { useWorkspaceGroups } from './hooks/useWorkspaceGroups';
import { useWorkspaceMessaging } from './hooks/useWorkspaceMessaging';
import { usePlazaFeed } from './hooks/usePlazaFeed';
import { useProfileSettings } from './hooks/useProfileSettings';
import { useFriendRequests } from './hooks/useFriendRequests';
import { useAdaptiveWorkspaceLayout } from './hooks/useAdaptiveWorkspaceLayout';
import {
  projectSessionMap,
  resolveSessionPresentation,
} from './services/sessionAvatarPresentation';
import { type ConnState, type LogEntry, SESSION_TYPE_GROUP, SESSION_TYPE_SINGLE } from '@shared/config/runtime';
import { tsNow, getErrorMessage } from '@shared/utils/ui';
import { closeRightDrawer } from '@shared/ui/rightDrawerStore';
import { blockUser } from '@domain/safety/blacklist';
import {
  composeConversationMessage,
  createConversationDraft,
  type ConversationDraft,
} from '@domain/conversation/draft';
import { fetchUserById, searchUsers } from '@domain/contacts/api';
import { IdentityCoordinator } from '@domain/identity/coordinator';
import { reconcileIdentityChanges } from '@domain/identity/reconnect';
import { IdentityRepository } from '@domain/identity/repository';
import { IdentityStore } from '@domain/identity/store';
import type { UserIdentity } from '@domain/identity/types';
import { applyPresenceNotification, applyPresenceSnapshot } from '@domain/presence/state';
import type { EmojiComposerHandle } from '../../emoji/EmojiComposer';
import type { SettingsSection } from './types/settings';
import type { CallHistoryEntry } from '@domain/call/types';

import { ImManager } from '../../im/imManager';
import { FileTransferManager } from '@domain/file/fileTransferManager';
import {
  requestFileTicket,
  requestFileTransferProjections,
  requestFileTransferSync,
  type FileTransferLifecycleSnapshot,
} from '@domain/file/api';
import { FileTransferRequestError, type FileTransferTask } from '@domain/file/types';
import { fileTransferSyncSignal } from '@domain/file/syncTrigger';
import {
  evaluateFileOperationCapability,
  realtimeTransferUnavailableMessage,
} from '@domain/file/capability';
import { fetchImBootstrap, getOrCreateSocketDeviceId } from '../../im/bootstrap';
import { loadLocalChatSnapshot } from '../../im/localChatSnapshot.js';
import { eventBus } from '../../eventBus';
import type { DirectoryMap, GroupMap, SessionMap } from '@domain/chat/types';
import { Center, Stack, Text, Box, Group, Loader, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { AuthSessionState } from '@domain/auth/types';
type GroupMemberEditMode = 'add' | 'remove';
type PendingImageDraft = {
  file: File;
  previewUrl: string;
};

let logIdCounter = 0;
let decryptText: ((payload: string, aesKey: string) => string) | null = null;
const BOOTSTRAPPING_HINT_DELAY_MS = 240;
const BOOTSTRAPPING_HINT_MIN_VISIBLE_MS = 700;
const TERMINAL_FILE_TRANSFER_STATES = new Set(['completed', 'failed', 'expired', 'cancelled']);
const REFRESHABLE_FILE_TRANSFER_STATES = new Set(['offered', 'claimed', 'streaming', 'reconnecting']);
const closedRegistrationPolicy: RegistrationPolicy = {
  registrationEnabled: false,
  passwordRegistrationEnabled: false,
  inviteCodeRequired: false,
  oauthLoginEnabled: false,
  oauthRegistrationEnabled: false,
  oauthProviders: [],
};

async function loadImRuntime() {
  const [{ ImClient: ImClientClass }, securityModule] = await Promise.all([
    import('../../im/imClient'),
    import('../../im/security'),
  ]);
  decryptText ??= securityModule.decryptMsg;
  return { ImClientClass };
}

function sessionKeyOf(sessionType: SessionKind, sessionId: number) {
  const typeNum = sessionType === 'group' ? 2 : 1;
  return `${typeNum}_${sessionId}`;
}

function sessionTypeToProtoValue(sessionType: SessionKind) {
  return sessionType === 'group' ? SESSION_TYPE_GROUP : SESSION_TYPE_SINGLE;
}

const uploadChatImage = (file: File, apiBase: string, session: ChatSession, metadata?: Record<string, unknown>): Promise<MediaAsset> => (
  uploadMedia(apiBase, file, {
    domain: 'chat',
    kind: 'image',
    visibility: 'chat_scope',
    scopeType: session.sessionType,
    scopeId: session.sessionId,
    metadata,
  })
);

const uploadChatFile = (file: File, apiBase: string, session: ChatSession): Promise<MediaAsset> => (
  uploadMedia(apiBase, file, {
    domain: 'chat',
    kind: 'file',
    visibility: 'chat_scope',
    scopeType: session.sessionType,
    scopeId: session.sessionId,
  })
);










/**
 * 判断两个消息是否可能是同一个消息（服务器回显 vs 本地乐观消息）
 */


function dedupeMessages(messages: ChatMessage[]) {
  console.log('[dedupeMessages] 开始去重，输入消息数量:', messages.length);
  console.log('[dedupeMessages] 输入消息详情:', messages.map(m => ({
    localId: m.localId,
    msgId: m.msgId,
    text: m.text.substring(0, 20) + (m.text.length > 20 ? '...' : ''),
    ts: new Date(m.ts).toISOString(),
    dir: m.dir,
    from: m.from
  })));
  
  const ordered = [...messages].sort((left, right) => {
    if (left.ts !== right.ts) {
      return left.ts - right.ts;
    }
    if (left.msgId !== right.msgId) {
      return left.msgId - right.msgId;
    }
    return left.localId.localeCompare(right.localId);
  });
  
  const map = new Map<string, ChatMessage>();
  const replacedLocalIds = new Set<string>();
  
  for (const message of ordered) {
    if (message.msgId > 0) {
      const identity = buildMessageIdentity(message);
      const existing = map.get(identity);

      if (existing) {
        console.log(`[dedupeMessages] 发现重复的服务器消息 identity=${identity}, 保留现有消息`);
      } else {
        console.log(`[dedupeMessages] 添加服务器消息 identity=${identity}, localId=${message.localId}`);
        map.set(identity, message);
      }
    } else {
      // 对于本地消息（msgId === 0），使用localId作为标识符
      const identity = `local-${message.localId}`;
      
      // 检查这个本地消息是否已经被服务器消息替换
      if (!replacedLocalIds.has(message.localId)) {
        console.log(`[dedupeMessages] 添加本地消息 localId=${message.localId}`);
        map.set(identity, message);
      } else {
        console.log(`[dedupeMessages] 跳过本地消息 localId=${message.localId}，已被服务器消息替换`);
      }
    }
  }
  
  const result = [...map.values()];
  console.log('[dedupeMessages] 去重完成，输出消息数量:', result.length);
  console.log('[dedupeMessages] 输出消息详情:', result.map(m => ({
    localId: m.localId,
    msgId: m.msgId,
    text: m.text.substring(0, 20) + (m.text.length > 20 ? '...' : ''),
    ts: new Date(m.ts).toISOString(),
    dir: m.dir,
    from: m.from
  })));
  
  return result;
}







function createSession(
  sessionType: SessionKind,
  sessionId: number,
  contactsById: DirectoryMap,
  groupsById: GroupMap,
): ChatSession {
  const presentation = resolveSessionPresentation(sessionType, sessionId, contactsById, groupsById);
  return {
    sessionKey: sessionKeyOf(sessionType, sessionId),
    sessionId,
    sessionType,
    title: presentation.title,
    subtitle: presentation.subtitle,
    avatarText: presentation.avatarText,
    updatedAt: Date.now(),
    lastMsgId: 0,
    lastMsgPreview: '',
    lastMsgFromUserId: 0,
    unreadCount: 0,
    messages: [],
    historyState: 'idle',
    historyError: null,
    hasMoreHistory: true,
  };
}

function groupMemberSummary(
  group: GroupMap[number] | undefined,
  contactsById: DirectoryMap,
  currentUserId: number,
) {
  if (!group || group.memberIds.length === 0) {
    return '还没有成员信息';
  }
  const labels = group.memberIds.slice(0, 3).map((memberId) => {
    if (memberId === currentUserId) {
      return '我';
    }
    return contactsById[memberId]?.name || `#${memberId}`;
  });
  const suffix = group.memberIds.length > 3 ? ` 等 ${group.memberIds.length} 人` : ` 共 ${group.memberIds.length} 人`;
  return `${labels.join('、')}${suffix}`;
}

function applyIdentitySnapshot(
  contacts: DirectoryMap,
  identities: ReadonlyMap<number, UserIdentity>,
): DirectoryMap {
  let changed = false;
  const next = { ...contacts };
  for (const [userIdText, contact] of Object.entries(contacts)) {
    const userId = Number(userIdText);
    const identity = identities.get(userId);
    if (!identity) continue;
    const name = identity.availability === 'ok' && identity.displayName
      ? identity.displayName
      : `用户 #${userId}`;
    const avatarUrl = identity.availability === 'ok'
      ? identity.avatar?.url ?? contact.avatarUrl
      : '';
    if (name !== contact.name || avatarUrl !== contact.avatarUrl ||
        contact.email !== '' || contact.phone !== '' || contact.signInfo !== '') {
      changed = true;
      next[userId] = {
        ...contact,
        name,
        avatarUrl,
        // Relationship directory state contains only stable IDs. These legacy
        // keys stay empty until the compatibility ContactDirectoryUser type is
        // removed; private/public profile detail never belongs in this map.
        email: '',
        phone: '',
        signInfo: '',
      };
    }
  }
  return changed ? next : contacts;
}



type WorkspaceAppProps = {
  initialAppConfig: AppConfig;
  initialAuthState: AuthSessionState;
};

export function WorkspaceApp({ initialAppConfig, initialAuthState }: WorkspaceAppProps) {
  const {
    apiBase,
    msgWsUrl,
    fileWsUrl,
    aesKey,
    cdnBase,
    email,
    setEmail,
    password,
    setPassword,
  } = useWorkspaceConfig();
  const [connState, setConnState] = useState<ConnState>('idle');
  const [imUserId, setImUserId] = useState<number | null>(null);
  const [imError, setImError] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [showBootstrappingHint, setShowBootstrappingHint] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const apiBaseRef = useRef(apiBase);
  apiBaseRef.current = apiBase;

  const [contactsById, setContactsById] = useState<DirectoryMap>({});
  const identityStoreRef = useRef(new IdentityStore());
  const [identitySnapshot, setIdentitySnapshot] = useState<ReadonlyMap<number, UserIdentity>>(
    () => identityStoreRef.current.snapshot(),
  );
  const identityRepositoryRef = useRef(new IdentityRepository());
  const identityCoordinatorRef = useRef<IdentityCoordinator | null>(null);
  const identityCoordinatorOwnerRef = useRef<number | null>(null);
  const identityHydrationPromiseRef = useRef<Promise<void> | null>(null);
  // Presence is socket-scoped runtime state. It deliberately lives outside
  // IdentityStore/IndexedDB, whose only authority is stable public profile
  // data. Reconnect always clears this state and obtains a fresh IM snapshot.
  const presenceRevisionByUserRef = useRef(new Map<number, number>());
  const [presenceByUserId, setPresenceByUserId] = useState<Record<number, number>>({});
  const [realtimeFileStartingSessionKey, setRealtimeFileStartingSessionKey] = useState<string | null>(null);
  const [realtimeFileNotice, setRealtimeFileNotice] = useState<string | null>(null);
  const realtimeFileStartingSessionKeyRef = useRef<string | null>(null);
  const [groupsById, setGroupsById] = useState<GroupMap>({});
  const [sessionsByKey, setSessionsByKey] = useState<SessionMap>({});
  const [activeSessionKey, setActiveSessionKey] = useState<string | null>(null);
  const [pendingInitialHistorySessionKey, setPendingInitialHistorySessionKey] = useState<string | null>(null);
  const [composerText, setComposerText] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [directoryFilter, setDirectoryFilter] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTab>('plaza');
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(null);
  const [contactsSubView, setContactsSubView] = useState<'list' | 'search' | 'requests' | 'groups' | 'group-search'>('list');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const adaptiveLayout = useAdaptiveWorkspaceLayout({ activeTab, activeSessionKey });

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [friendRequestNote, setFriendRequestNote] = useState('');
  const [friendRequestTarget, setFriendRequestTarget] = useState<UserSearchResult | null>(null);
  const [isFriendRequestModalOpen, setIsFriendRequestModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingImageDraft, setPendingImageDraft] = useState<PendingImageDraft | null>(null);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isGroupDetailModalOpen, setIsGroupDetailModalOpen] = useState(false);
  const [isGroupMemberModalOpen, setIsGroupMemberModalOpen] = useState(false);
  const [groupMemberEditMode, setGroupMemberEditMode] = useState<GroupMemberEditMode>('add');
  const [groupMemberDraftIds, setGroupMemberDraftIds] = useState<number[]>([]);
  const [groupMemberSubmitting, setGroupMemberSubmitting] = useState(false);
  const [conversationDraftTarget, setConversationDraftTarget] = useState<ContactDirectoryUser | null>(null);
  const [conversationDraft, setConversationDraft] = useState<ConversationDraft>(createConversationDraft);
  const [conversationDraftSubmitting, setConversationDraftSubmitting] = useState(false);
  const [blockingUserId, setBlockingUserId] = useState<number | null>(null);

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [viewingProfileUser, setViewingProfileUser] = useState<ContactDirectoryUser | null>(null);
  const [isProfileViewOpen, setIsProfileViewOpen] = useState(false);
  const [profileRelationIsFriend, setProfileRelationIsFriend] = useState(false);
  const [profileRelationIsPending, setProfileRelationIsPending] = useState(false);
  const [loadingProfileUser, setLoadingProfileUser] = useState(false);

  const {
    state: authState,
    refreshSession,
    login,
    loginWithOAuth,
    logout,
    register,
    resendRegistration,
    finishRegistration,
    requestPasswordReset,
    resetPassword,
  } = useAuthSession(apiBase, initialAuthState);
  const oauthOptions = useOAuthLoginOptions(loginWithOAuth);
  const [registrationPolicy] = useState(initialAppConfig.registrationPolicy ?? closedRegistrationPolicy);
  const currentUserId = authState.user?.id ?? 0;
  const presentedSessionsByKey = useMemo(
    () => projectSessionMap(sessionsByKey, contactsById, groupsById),
    [contactsById, groupsById, sessionsByKey],
  );

  const imRef = useRef<ImClient | null>(null);
  const sessionsRef = useRef(presentedSessionsByKey);
  const contactsRef = useRef(contactsById);
  const groupsRef = useRef(groupsById);
  const activeSessionKeyRef = useRef(activeSessionKey);
  const imUserIdRef = useRef(imUserId);
  const authUserIdRef = useRef<number | null>(authState.user?.id ?? null);
  const imBootstrappingUserIdRef = useRef<number | null>(null);
  const localChatSnapshotUserIdRef = useRef<number | null>(null);
  const initialHistoryLoadingSessionRef = useRef<string | null>(null);
  const bootstrappingHintShownAtRef = useRef<number | null>(null);
  const bootstrappingHintHideTimerRef = useRef<number | null>(null);
  const composerRef = useRef<EmojiComposerHandle | null>(null);
  const feedEnd = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const emojiToggleRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => () => {
    setPendingImageDraft((current) => {
      if (current) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
  }, []);

  useEffect(() => identityStoreRef.current.subscribe(() => {
    const snapshot = identityStoreRef.current.snapshot();
    // Publish the exact same immutable map to React that drives the contact
    // projection. Profile overlays can then observe version changes directly
    // instead of retaining a second, stale identity snapshot.
    setIdentitySnapshot(snapshot);
    setContactsById((current) => applyIdentitySnapshot(current, snapshot));
  }), []);

  const viewingProfileIdentity = viewingProfileUser
    ? identitySnapshot.get(viewingProfileUser.id)
    : undefined;
  const effectiveViewingProfileUser = useMemo(() => {
    if (!viewingProfileUser || viewingProfileIdentity?.availability !== 'ok') {
      return viewingProfileUser;
    }
    return {
      ...viewingProfileUser,
      name: viewingProfileIdentity.displayName || viewingProfileUser.name,
      // A null canonical avatar means "use the deterministic local default";
      // retaining an older URL here would recreate the cross-screen mismatch.
      avatarUrl: viewingProfileIdentity.avatar?.url ?? '',
    };
  }, [viewingProfileIdentity, viewingProfileUser]);

  const ensureIdentityCoordinator = useCallback(async (ownerUserId: number) => {
    if (identityCoordinatorOwnerRef.current !== ownerUserId ||
        !identityCoordinatorRef.current) {
      identityStoreRef.current.clear();
      const coordinator = new IdentityCoordinator(
        ownerUserId,
        identityRepositoryRef.current,
        identityStoreRef.current,
      );
      identityCoordinatorRef.current = coordinator;
      identityCoordinatorOwnerRef.current = ownerUserId;
      const hydration = coordinator.hydrateCache(() => (
        identityCoordinatorOwnerRef.current === ownerUserId &&
        identityCoordinatorRef.current === coordinator
      )).finally(() => {
        if (identityHydrationPromiseRef.current === hydration) {
          identityHydrationPromiseRef.current = null;
        }
      });
      identityHydrationPromiseRef.current = hydration;
    }
    await identityHydrationPromiseRef.current;
    return identityCoordinatorRef.current!;
  }, []);

  const pushLog = useCallback((msg: string, kind: LogEntry['kind'] = 'info') => {
    setLogs((prev) => [
      ...prev.slice(-199),
      { id: ++logIdCounter, ts: tsNow(), msg, kind },
    ]);
  }, []);

  const [fileTransferTasks, setFileTransferTasks] = useState<FileTransferTask[]>([]);
  const [fileTransferLifecycleById, setFileTransferLifecycleById] = useState<Record<string, FileTransferLifecycleSnapshot | null | false>>({});
  const fileTransferSyncInFlightRef = useRef(false);
  const fileTransferSyncPendingRef = useRef(false);
  const fileTransferManagerRef = useRef<FileTransferManager | null>(null);
  if (!fileTransferManagerRef.current) {
    fileTransferManagerRef.current = new FileTransferManager({
      onLog: pushLog,
      requestFileTicket: async ({ taskId, fileRole, deviceId }) => {
        const response = await requestFileTicket(apiBaseRef.current, { taskId, fileRole, deviceId });
        return response.ticket;
      },
    });
  }
  useEffect(() => fileTransferManagerRef.current?.subscribe(setFileTransferTasks), []);


  useEffect(() => {
    if (!isProfileViewOpen || !viewingProfileUser ||
        viewingProfileUser.id === currentUserId) {
      return undefined;
    }

    const identity = identityStoreRef.current.get(viewingProfileUser.id);
    const loadedVersion = viewingProfileUser.profileVersion ?? 0;
    const requiredVersion = identity?.availability === 'ok'
      ? identity.profileVersion
      : 1;
    if (loadedVersion >= requiredVersion) {
      return undefined;
    }

    // The batch identity endpoint intentionally contains only list-card fields.
    // When its version advances, refresh the complete public page snapshot so
    // signature and homepage background move forward with name and avatar.
    let cancelled = false;
    const viewedUserId = viewingProfileUser.id;
    void fetchUserById(viewedUserId, apiBase)
      .then((publicProfile) => {
        if (cancelled) return;
        setViewingProfileUser((current) => current?.id === viewedUserId
          ? publicProfile
          : current);
      })
      .catch((error) => {
        if (!cancelled) {
          pushLog(`用户主页暂时无法刷新: ${getErrorMessage(error)}`, 'warn');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    apiBase,
    currentUserId,
    identitySnapshot,
    isProfileViewOpen,
    pushLog,
    viewingProfileUser?.id,
    viewingProfileUser?.profileVersion,
  ]);

  const clearPendingImageDraft = useCallback(() => {
    setPendingImageDraft((current) => {
      if (current) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
  }, []);

  const {
    profileDraft,
    profileSnapshot,
    profileLoadState,
    profileError,
    profileDirty,
    profileUploadingField,
    resetProfileState,
    handleProfileDraftChange,
    handleProfileImagePick,
    handleResetProfileDraft,
    handleSaveProfile,
  } = useProfileSettings({
    apiBase,
    authUser: authState.user,
    contactsById,
    refreshSession,
    setContactsById,
    pushLog,
  });

  const {
    friendRequests,
    friendRequestsLoading,
    friendRequestActionById,
    pendingRequestCount,
    resetFriendRequestsState,
    loadFriendRequests,
    refreshPendingCount,
    handleApproveRequest,
    handleRejectRequest,
  } = useFriendRequests({
    apiBase,
    isAuthenticated: authState.status === 'authenticated',
    pushLog,
    onApproved: async () => {
      if (imRef.current && imUserIdRef.current != null) {
        await ImManager.getInstance().initData();
      }
    },
  });

  const {
    searchKeyword,
    setSearchKeyword,
    searchResults,
    setSearchResults,
    searchState,
    setSearchState,
    searchError,
    setSearchError,
    groupSearchKeyword,
    setGroupSearchKeyword,
    groupSearchResults,
    setGroupSearchResults,
    groupSearchState,
    setGroupSearchState,
    groupSearchError,
    setGroupSearchError,
    sendingRequestByUserId,
    resetDirectoryState,
    handleSearchUsers,
    handleSearchGroups,
    handleSendFriendRequest,
  } = useWorkspaceDirectory({
    apiBase,
    pushLog,
    refreshPendingCount,
  });

  const {
    plazaArticles,
    plazaHasMore,
    plazaLoadState,
    plazaError,
    setIsPlazaAtTop,
    plazaRefreshSource,
    isPostModalOpen,
    setIsPostModalOpen,
    postTitle,
    setPostTitle,
    postContent,
    setPostContent,
    postTags,
    setPostTags,
    postSubmitting,
    postSubmitStatus,
    postSubmitError,
    postImages,
    postVideo,
    postLocation,
    postLocationPending,
    canSubmitPost,
    closePostComposer,
    loadPlazaArticles,
    handleSubmitPost,
    handleAddPostImages,
    handleRemovePostImage,
    handleSelectPostVideo,
    handleRemovePostVideo,
    handleUseCurrentLocation,
    removePostLocation,
    handleToggleArticleLike,
    handleToggleArticleFavorite,
  } = usePlazaFeed({
    activeTab,
    apiBase,
    isAuthenticated: authState.status === 'authenticated',
    pushLog,
  });
  useEffect(() => {
    sessionsRef.current = presentedSessionsByKey;
  }, [presentedSessionsByKey]);

  useEffect(() => {
    contactsRef.current = contactsById;
  }, [contactsById]);

  useEffect(() => {
    groupsRef.current = groupsById;
  }, [groupsById]);

  useEffect(() => {
    activeSessionKeyRef.current = activeSessionKey;
    ImManager.getInstance().setCurrentSessionKey(activeSessionKey);
  }, [activeSessionKey]);


  useEffect(() => {
    imUserIdRef.current = imUserId;
  }, [imUserId]);

  useEffect(() => {
    authUserIdRef.current = authState.status === 'authenticated'
      ? authState.user?.id ?? null
      : null;
  }, [authState.status, authState.user?.id]);

  const hydrateLocalWorkspaceState = useCallback(async (userId: number) => {
    // Opening is idempotent for the same owner and closes any previously open
    // account namespace before local state is read.
    await chatRepository.openForOwner(userId);
    await ensureIdentityCoordinator(userId);
    const snapshot = await loadLocalChatSnapshot({
      repository: chatRepository,
      currentUserId: userId,
      resolveSessionPresentation,
    });

    if (authUserIdRef.current !== userId) {
      return;
    }

    localChatSnapshotUserIdRef.current = userId;
    setContactsById(applyIdentitySnapshot(
      snapshot.contactsById as DirectoryMap,
      identityStoreRef.current.snapshot(),
    ));
    setGroupsById(snapshot.groupsById as GroupMap);
    setSessionsByKey((prev) => {
      const next: SessionMap = { ...(snapshot.sessionsByKey as SessionMap) };
      for (const [sessionKey, session] of Object.entries(prev)) {
        const localSession = next[sessionKey];
        if (!localSession) {
          continue;
        }
        next[sessionKey] = {
          ...localSession,
          messages: session.messages,
          historyState: session.historyState,
          historyError: session.historyError,
          hasMoreHistory: session.hasMoreHistory,
        };
      }
      return next;
    });
  }, [ensureIdentityCoordinator]);

  const {
    callBootstrap,
    callBootstrapError,
    callConnectionState,
    callUiState,
    activeCall,
    incomingCall,
    isMuted,
    isVideoEnabled,
    callAudioLevels,
    localCallMediaRef,
    remoteCallMediaRef,
    callLocalStream,
    callRemoteStream,
    handleStartCall,
    handleRespondIncomingCall,
    handleHangupCall,
    handleToggleMute,
    handleToggleVideo,
  } = useWorkspaceCall({
    apiBase,
    authStatus: authState.status,
    authUser: authState.user,
    contactsRef,
    sessionsRef,
    activeSessionKeyRef,
    pushLog,
    sessionKeyOf,
  });

  const {
    connectionState: realtimeConnectionState,
    incomingSession: incomingRealtimeSession,
    activeSession: activeRealtimeSession,
    localStream: realtimeLocalStream,
    remoteStreams: realtimeRemoteStreams,
    remoteBoardPatch,
    remoteBoardSnapshot,
    startSession: startRealtimeSession,
    acceptIncoming: acceptIncomingRealtimeSession,
    rejectIncoming: rejectIncomingRealtimeSession,
    endSession: endRealtimeSession,
    sendBoardPatch,
  } = useRealtimeCollaboration({
    apiBase,
    bootstrap: callBootstrap,
    currentUserId: authState.user?.id ?? 0,
    currentUserName: authState.user?.displayName || authState.user?.email || 'Locs 用户',
    onNotice: (message, tone = 'info') => pushLog(message, tone === 'error' ? 'error' : 'info'),
  });

  useEffect(() => {
    const unsub1 = eventBus.on('ContactsUpdateEvent', ({ contacts, groups }) => {
      const selfUserId = imUserIdRef.current ?? authState.user?.id ?? 0;
      const filteredContacts = Object.fromEntries(
        Object.entries(contacts).filter(([id]) => Number(id) !== selfUserId),
      ) as DirectoryMap;
      setContactsById(filteredContacts);
      setGroupsById(groups);
      const userIds = Object.keys(filteredContacts).map(Number).filter((id) => id > 0);
      if (userIds.length > 0) {
        const coordinator = identityCoordinatorRef.current;
        if (coordinator) {
          void (async () => {
            // The public endpoint is deliberately bounded at 200 identities.
            // Process every relationship ID in stable chunks instead of
            // silently truncating a large directory at its first page.
            for (let offset = 0; offset < userIds.length; offset += 200) {
              await coordinator.fetchAndCommit(
                userIds.slice(offset, offset + 200).map((userId) => ({
                  userId,
                  minProfileVersion: 0,
                })),
                apiBase,
              );
            }
          })().catch((error) => {
            pushLog(`刷新用户公开资料失败: ${getErrorMessage(error)}`, 'warn');
          });
        }
      }
    });
    const unsub2 = eventBus.on('SessionUpdateEvent', async (list) => {
      const selfUserId = imUserIdRef.current ?? authState.user?.id ?? 0;
      const map: SessionMap = {};
      for (const s of list.filter((session) => (
        !(session.sessionType === 'single' && session.sessionId === selfUserId)
      ))) {
        map[s.sessionKey] = {
           ...s,
           messages: [],
           historyState: 'ready',
           historyError: null,
           hasMoreHistory: true
        } as any;
      }
      setSessionsByKey(prev => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(map)) {
           next[k] = { ...v, messages: prev[k]?.messages ?? [] };
        }
        return next;
      });
    });
    const unsub3 = eventBus.on('NewMsgEvent', async (msg) => {
      if (activeSessionKeyRef.current === msg.sessionKey) {
          const fresh = await chatRepository.getMessagesBySession(msg.sessionKey, 50);
          setSessionsByKey(prev => ({
             ...prev,
             [msg.sessionKey]: {
                 ...(prev[msg.sessionKey] as any),
                 messages: dedupeMessages(fresh.slice().reverse()),
                 unreadCount: 0,
             }
          }));
      }
    });
    const unsub4 = eventBus.on('HistoryGapFilledEvent', async (sessionKey) => {
      if (activeSessionKeyRef.current === sessionKey) {
          const fresh = await chatRepository.getMessagesBySession(sessionKey, 50);
          setSessionsByKey(prev => ({
             ...prev,
             [sessionKey]: {
                 ...prev[sessionKey],
                 messages: dedupeMessages(fresh.slice().reverse()),
                 unreadCount: 0,
             }
          }));
      }
    });

    return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
  }, [apiBase, authState.user?.id, pushLog]);
  const disconnectIm = useCallback((message?: string, kind: LogEntry['kind'] = 'warn') => {
    const client = imRef.current;
    const hadSession = client != null || connState !== 'idle';
    imRef.current = null;
    client?.close();
    fileTransferManagerRef.current?.reset();
    setFileTransferTasks([]);
    setFileTransferLifecycleById({});
    setImUserId(null);
    setConnState('idle');
    setImError(null);
    setIsBootstrapping(false);
    presenceRevisionByUserRef.current.clear();
    setPresenceByUserId({});
    if (hadSession && message) {
      pushLog(message, kind);
    }
  }, [connState, pushLog]);


  const establishImSession = useCallback(async (
    user: AuthSessionUser,
  ) => {
    if (imRef.current && imUserIdRef.current === user.id && connState === 'online') {
      return;
    }
    if (imBootstrappingUserIdRef.current === user.id) {
      return;
    }

    imBootstrappingUserIdRef.current = user.id;
    disconnectIm();
    // IM bootstrap may finish faster than the React startup hydration effect.
    // Open the same account-scoped repository here as an idempotent ordering
    // barrier before ImManager attempts any session/message persistence.
    try {
      await chatRepository.openForOwner(user.id);
      await ensureIdentityCoordinator(user.id);
    } catch (error) {
      imBootstrappingUserIdRef.current = null;
      setConnState('error');
      setImError(getErrorMessage(error));
      pushLog(`本地账户缓存初始化失败: ${getErrorMessage(error)}`, 'error');
      return;
    }
    setIsBootstrapping(true);
    setConnState('connecting');
    setImError(null);
    pushLog('正在连接 IM 服务…', 'info');

    const { ImClientClass } = await loadImRuntime();

    const client = new ImClientClass(
      msgWsUrl,
      {
        onStatus: (message) => {
          const kind: LogEntry['kind'] =
            message.includes('closed') || message.includes('error')
              ? 'error'
              : message.includes('open')
                ? 'success'
                : 'info';
          pushLog(`WS: ${message}`, kind);
          if (message.includes('closed') && imRef.current === client) {
            setConnState('error');
            setImUserId(null);
          }
        },
        onChatMessage: (message) => {
          if (imRef.current === client) {
            ImManager.getInstance().handleIncomingMessage(message);
          }
        },
        onFileNotify: (notify) => {
          if (imRef.current === client) {
            fileTransferManagerRef.current?.handleFileNotify(notify);
          }
        },
        onProfileChanged: ({ userId, profileVersion }) => {
          if (imRef.current !== client) return;
          const coordinator = identityCoordinatorRef.current;
          if (!coordinator) return;

          // Realtime notifications are invalidations, not profile payloads.
          // The coordinator coalesces bursts and hydrates canonical public data
          // over HTTP. Do not persist the notification's sparse change_cursor;
          // reconnect synchronization owns durable cursor advancement.
          void coordinator.refreshInvalidated(
            { userId, minProfileVersion: profileVersion },
            apiBase,
          ).catch((error) => {
            pushLog(`刷新实时用户资料失败: ${getErrorMessage(error)}`, 'warn');
          });
        },
        onUserPresenceChanged: ({ userId, status }: ImUserPresence) => {
          if (imRef.current !== client) return;
          // Increment a per-user revision before publishing the notification.
          // Snapshot responses use this revision to avoid overwriting a newer
          // notification that arrived while their request was in flight.
          presenceRevisionByUserRef.current.set(
            userId,
            (presenceRevisionByUserRef.current.get(userId) ?? 0) + 1,
          );
          setPresenceByUserId((current) => {
            return applyPresenceNotification(current, { userId, status });
          });
        },
      },
      aesKey,
    );

    try {
      const bootstrap = await fetchImBootstrap(apiBase);
      const deviceId = getOrCreateSocketDeviceId();
      await client.connect();
      const response = await client.loginIm(bootstrap.token, deviceId);
      const authenticatedUser = response.getAuthenticatedUser?.();
      const userId = authenticatedUser?.getUserId?.() ?? null;
      if (userId == null) {
        throw new Error('服务端未返回 IM userId');
      }
      if (userId !== user.id) {
        throw new Error(`IM userId(${userId}) 与当前登录账号(${user.id}) 不一致`);
      }

      imRef.current = client;
      fileTransferManagerRef.current?.bind({ client, userId, wsUrl: fileWsUrl });
      setImUserId(userId);
      setConnState('online');
      setImError(null);
      pushLog(`实时连接已建立，imUserId=${userId}`, 'success');
      ImManager.getInstance().attachClient(client, userId);
      await ImManager.getInstance().initData(apiBase);
      const coordinator = identityCoordinatorRef.current;
      if (coordinator) {
        await reconcileIdentityChanges({
          ownerUserId: userId,
          repository: identityRepositoryRef.current,
          coordinator,
          baseUrl: apiBase,
          loadPage: ({ afterChangeCursor, throughChangeCursor, limit }) => (
            client.requestProfileChanges(afterChangeCursor, throughChangeCursor, limit)
          ),
        }).then(async (result) => {
          if (result.status === 'full_snapshot_required') {
            // Rebuild from relationship/session IDs, not cached profile rows.
            // Only after every bounded HTTP page commits may the safe IM
            // high-water cursor become durable.
            const relevantIds = ImManager.getInstance().relevantIdentityUserIds();
            for (let offset = 0; offset < relevantIds.length; offset += 200) {
              await coordinator.fetchAndCommit(
                relevantIds.slice(offset, offset + 200).map((subjectUserId) => ({
                  userId: subjectUserId,
                  minProfileVersion: 0,
                })),
                apiBase,
              );
            }
            await coordinator.commitReconnectCursor(result.throughChangeCursor);
            pushLog('用户资料索引已从当前关系与会话完整重建', 'success');
          }
        }).catch((error) => {
          // Cached identities remain usable and every successfully applied page
          // is durable. A later reconnect resumes from the last committed page.
          pushLog(`同步离线用户资料失败: ${getErrorMessage(error)}`, 'warn');
        });
      }
    } catch (error) {
      if (imRef.current === client) {
        imRef.current = null;
      }
      client.close();
      setConnState('error');
      setImUserId(null);
      setImError(getErrorMessage(error));
      pushLog(`IM 连接失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      imBootstrappingUserIdRef.current = null;
      setIsBootstrapping(false);
    }
  }, [aesKey, apiBase, connState, disconnectIm, ensureIdentityCoordinator, fileWsUrl, pushLog, msgWsUrl]);

  const presenceSubjectKey = useMemo(() => {
    // Direct sessions are valid presence subjects even when the peer is not a
    // contact. The union prevents a conversation-only peer from remaining in
    // the misleading "unknown" state forever.
    const subjectIds = new Set(Object.keys(contactsById).map(Number));
    Object.values(sessionsByKey).forEach((session) => {
      if (session.sessionType === 'single') subjectIds.add(session.sessionId);
    });
    return [...subjectIds]
      .filter((userId) => userId > 0 && userId !== imUserId)
      .sort((left, right) => left - right)
      .join(',');
  }, [contactsById, imUserId, sessionsByKey]);

  useEffect(() => {
    const client = imRef.current;
    if (connState !== 'online' || !client || !presenceSubjectKey) return;

    const subjectIds = presenceSubjectKey.split(',').map(Number);
    let cancelled = false;
    void (async () => {
      // The client boundary is capped at 200 IDs per PDU. Chunking keeps a
      // large relationship directory bounded without changing server protocol.
      for (let offset = 0; offset < subjectIds.length; offset += 200) {
        const chunk = subjectIds.slice(offset, offset + 200);
        const revisionsAtRequest = new Map(chunk.map((userId) => [
          userId,
          presenceRevisionByUserRef.current.get(userId) ?? 0,
        ]));
        const snapshot = await client.requestUsersPresence(chunk);
        if (cancelled || imRef.current !== client) return;
        // A notification received after this request started is newer than
        // its snapshot row and therefore wins.
        setPresenceByUserId((current) => applyPresenceSnapshot(
          current,
          snapshot,
          revisionsAtRequest,
          presenceRevisionByUserRef.current,
        ));
      }
    })().catch((error) => {
      if (!cancelled && imRef.current === client) {
        pushLog(`同步联系人在线状态失败: ${getErrorMessage(error)}`, 'warn');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [connState, presenceSubjectKey, pushLog]);

  const handleLogin = useCallback(async (credentials: { email: string; password: string }) => {
    if (!credentials.email || !credentials.password) {
      pushLog('请输入邮箱和密码', 'warn');
      return;
    }

    pushLog('正在登录…', 'info');
    try {
      const user = await login(credentials);
      clearCredentials();
      pushLog(`登录成功，欢迎回来 ${user.displayName || user.email || user.id}`, 'success');
      await establishImSession(user);
    } catch {
      /* state already updated */
    }
  }, [establishImSession, login, pushLog]);

  const handleReconnectIm = useCallback(async () => {
    if (authState.status !== 'authenticated' || !authState.user) {
      pushLog('请先完成登录', 'warn');
      return;
    }
    try {
      await establishImSession(authState.user);
    } catch {
      /* handled in establishImSession */
    }
  }, [authState.status, authState.user, email, establishImSession, password, pushLog]);

  const handleRefreshSession = useCallback(async () => {
    pushLog('正在更新咨询与社区状态…', 'info');
    try {
      const user = await refreshSession();
      if (user) {
        pushLog(`登录状态有效，userId=${user.id}`, 'success');
      } else {
        pushLog('当前没有有效登录状态', 'warn');
      }
    } catch (error) {
      pushLog(`状态更新失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [pushLog, refreshSession]);

  const {
    isAudioMode,
    setIsAudioMode,
    isRecordingAudio,
    recordingLabel,
    recordingLevels,
    loadHistory,
    handleSendMessage,
    handleSendImage,
    handleStartAudioRecording,
    handleStopAudioRecording,
  } = useWorkspaceMessaging({
    connState,
    composerText,
    setComposerText,
    pushLog,
    imRef,
    imUserIdRef,
    sessionsRef,
    activeSessionKeyRef,
    setSessionsByKey,
    composerRef,
    sessionTypeToProtoValue,
    dedupeMessages,
    uploadImage: uploadChatImage,
    apiBase,
  });

  const handleChatImageSelect = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    setPendingImageDraft((current) => {
      if (current) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return {
        file,
        previewUrl: URL.createObjectURL(file),
      };
    });
  }, []);

  const handleConfirmChatImageSend = useCallback(async () => {
    if (!pendingImageDraft) {
      return;
    }
    const file = pendingImageDraft.file;
    clearPendingImageDraft();
    await handleSendImage(file);
  }, [clearPendingImageDraft, handleSendImage, pendingImageDraft]);

  const handleChatFileSelect = useCallback(async (file: File) => {
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;

    if (connState !== 'online') {
      pushLog('IM 未在线，无法发送文件', 'warn');
      return;
    }
    if (!session) {
      pushLog('请先选择一个会话', 'warn');
      return;
    }
    if (session.sessionType !== 'single') {
      pushLog('群聊文件消息暂不支持', 'warn');
      return;
    }
    const fileSizeError = chatFileSizeError(file.size);
    if (fileSizeError) {
      notifications.show({
        title: '文件太大',
        message: fileSizeError,
        color: 'red',
        autoClose: 5000,
      });
      pushLog(fileSizeError, 'warn');
      return;
    }

    // Show the user's action immediately while the private asset is uploading.
    // The placeholder is UI-only; only the completed asset payload is persisted
    // or sent over IM.
    const localId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const timestamp = Date.now();
    const pendingFileMeta: ChatFileMeta = {
      assetId: `pending:${localId}`,
      provider: 's3',
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      schemaVersion: 2,
    };
    const pendingMessage: ChatMessage = {
      localId,
      msgId: 0,
      sessionKey: session.sessionKey,
      sessionId: session.sessionId,
      sessionType: session.sessionType,
      from: imUserIdRef.current ?? currentUserId,
      text: `[文件] ${file.name}`,
      fileMeta: pendingFileMeta,
      ts: timestamp,
      dir: 'out',
      status: 'pending',
    };
    setSessionsByKey((prev) => {
      const current = prev[session.sessionKey] ?? session;
      return {
        ...prev,
        [session.sessionKey]: {
          ...current,
          updatedAt: timestamp,
          lastMsgPreview: pendingMessage.text,
          lastMsgFromUserId: pendingMessage.from,
          messages: dedupeMessages([...current.messages, pendingMessage]),
        },
      };
    });

    try {
      const media = await uploadChatFile(file, apiBase, session);
      await ImManager.getInstance().sendFileMsg({
        assetId: media.id,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
      }, session.sessionId, localId);
      pushLog(`离线文件已上传并发送：${file.name}`, 'success');
    } catch (error) {
      const message = getErrorMessage(error);
      setSessionsByKey((prev) => {
        const current = prev[session.sessionKey];
        if (!current) return prev;
        return {
          ...prev,
          [session.sessionKey]: {
            ...current,
            messages: current.messages.map((item) => (
              item.localId === localId ? { ...item, status: 'failed' as const } : item
            )),
          },
        };
      });
      notifications.show({ title: '文件发送失败', message, color: 'red', autoClose: 6000 });
      pushLog(`文件发送失败: ${message}`, 'error');
    }
  }, [apiBase, connState, currentUserId, dedupeMessages, imUserIdRef, pushLog, setSessionsByKey, sessionsRef, activeSessionKeyRef]);

  const handleRealtimeFileSelect = useCallback(async (file: File) => {
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    const manager = fileTransferManagerRef.current;
    const expectedSessionKey = session?.sessionKey ?? null;
    const capability = evaluateFileOperationCapability({
      imConnected: connState === 'online',
      sessionKnown: session != null,
      singleSession: session?.sessionType === 'single',
      sessionCurrent: expectedSessionKey != null && activeSessionKeyRef.current === expectedSessionKey,
      realtimeRuntimeReady: manager != null,
      peerPresence: session?.sessionType === 'single'
        ? presenceByUserId[session.sessionId]
        : undefined,
    });
    if (!capability.realtimeAvailable || !session || !manager) {
      console.info('[file-capability] realtime_send_guard_rejected', {
        reason: capability.reason,
        sessionType: session?.sessionType ?? null,
        peerUserId: session?.sessionType === 'single' ? session.sessionId : null,
      });
      setRealtimeFileNotice(realtimeTransferUnavailableMessage(capability.reason));
      return;
    }
    if (realtimeFileStartingSessionKeyRef.current === expectedSessionKey) return;
    realtimeFileStartingSessionKeyRef.current = expectedSessionKey;
    setRealtimeFileNotice(null);
    setRealtimeFileStartingSessionKey(expectedSessionKey);
    let transferTaskId: string | null = null;
    try {
      const task = await manager.sendFile(file, session.sessionId, 'online');
      if (activeSessionKeyRef.current !== expectedSessionKey) {
        manager.cancelTask(task.taskId, '会话已切换');
        console.info('[file-capability] realtime_send_guard_rejected', { reason: 'stale_session' });
        return;
      }
      transferTaskId = task.taskId;
      const message = await ImManager.getInstance().sendFileMsg({
        fileId: crypto.randomUUID(),
        transferId: task.taskId,
        transport: 'online',
        fileName: file.name,
        fileSize: file.size,
        sha256: task.sha256,
        mimeType: file.type || 'application/octet-stream',
      }, session.sessionId);
      if (message.status !== 'sent') {
        manager.cancelTask(task.taskId, '实时传输消息发送失败');
        transferTaskId = null;
        throw new Error('实时传输消息未送达');
      }
      transferTaskId = null;
      pushLog(`实时传输已发起：${file.name}`, 'success');
    } catch (error) {
      if (transferTaskId) manager.cancelTask(transferTaskId, '实时传输发起失败');
      const resultCode = error instanceof FileTransferRequestError ? error.resultCode : null;
      console.warn('[file-capability] realtime_request_rejected', {
        reason: resultCode === 1 ? 'peer_offline' : 'unknown_result_code',
        resultCode,
        peerUserId: session.sessionId,
      });
      setRealtimeFileNotice(resultCode === 1
        ? '对方当前不在线，实时传输未开始'
        : '实时传输暂时无法开始');
    } finally {
      if (realtimeFileStartingSessionKeyRef.current === expectedSessionKey) {
        realtimeFileStartingSessionKeyRef.current = null;
      }
      setRealtimeFileStartingSessionKey((current) => current === expectedSessionKey ? null : current);
    }
  }, [connState, presenceByUserId, pushLog, sessionsRef, activeSessionKeyRef]);

  const handleAcceptFileTask = useCallback(async (assetId: string, fileName?: string) => {
    try {
      await downloadChatFile(assetId, fileName || 'download.bin', apiBase);
      pushLog('下载请求已提交；如浏览器要求选择保存位置，请确认保存', 'success');
    } catch (error) {
      const message = getErrorMessage(error);
      notifications.show({ title: '下载失败', message, color: 'red', autoClose: 6000 });
      pushLog(`接收文件失败: ${message}`, 'error');
    }
  }, [apiBase, pushLog]);

  const handleAcceptRealtimeFile = useCallback(async (
    taskId: string,
    expected: { fileName: string; fileSize: number; sha256?: string },
  ) => {
    try {
      await fileTransferManagerRef.current?.acceptTask(taskId, expected);
    } catch (error) {
      const message = getErrorMessage(error);
      notifications.show({ title: '实时传输接收失败', message, color: 'red', autoClose: 6000 });
      pushLog(`实时传输接收失败: ${message}`, 'error');
    }
  }, [pushLog]);

  const handleBlockUser = useCallback(async (userId: number, name: string) => {
    if (currentUserId === userId) {
      pushLog('不能屏蔽自己', 'warn');
      return;
    }
    const confirmed = window.confirm(`屏蔽 ${name}？屏蔽后将移除当前关系和单聊会话。`);
    if (!confirmed) {
      return;
    }

    const key = sessionKeyOf('single', userId);
    setBlockingUserId(userId);
    try {
      await blockUser(userId, 'Blocked', apiBase);
      await chatRepository.deleteSession(key);
      setContactsById((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      contactsRef.current = Object.fromEntries(
        Object.entries(contactsRef.current).filter(([id]) => Number(id) !== userId),
      ) as DirectoryMap;
      setSessionsByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      delete sessionsRef.current[key];
      if (activeSessionKeyRef.current === key) {
        activeSessionKeyRef.current = null;
        setActiveSessionKey(null);
        ImManager.getInstance().setCurrentSessionKey(null);
      }
      pushLog(`已屏蔽 ${name}`, 'success');
    } catch (error) {
      pushLog(`屏蔽失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setBlockingUserId(null);
    }
  }, [apiBase, currentUserId, pushLog, sessionKeyOf]);

  const openSession = useCallback((sessionKey: string) => {
    closeRightDrawer();
    setActiveTab('chats');
    setActiveSessionKey(sessionKey);
    setPendingInitialHistorySessionKey(sessionKey);
    adaptiveLayout.showStageOnTablet();
    activeSessionKeyRef.current = sessionKey;
    ImManager.getInstance().setCurrentSessionKey(sessionKey);
    setComposerText('');
    setSessionsByKey((prev) => {
      const current = prev[sessionKey];
      if (!current) return prev;
      return {
        ...prev,
        [sessionKey]: {
          ...current,
          unreadCount: 0,
          historyState: current.messages.length === 0 ? 'loading' : current.historyState,
          historyError: current.messages.length === 0 ? null : current.historyError,
        },
      };
    });
    // Persistent clear in DB and memory
    void ImManager.getInstance().clearUnReadCntBySessionKey(sessionKey);
    window.setTimeout(() => composerRef.current?.focus(), 20);
  }, [adaptiveLayout]);

  const handleBackToList = useCallback(() => {
    setActiveSessionKey(null);
    setComposerText('');
    setIsEmojiPickerOpen(false);
    adaptiveLayout.showListOnTablet();
    ImManager.getInstance().setCurrentSessionKey(null);
  }, [adaptiveLayout]);

  const handleWorkspaceNavigate = useCallback((value: ActiveTab) => {
    closeRightDrawer();
    setActiveTab(value);
    if (value === 'contacts') {
      setContactsSubView('list');
    }
    if (value !== 'chats') {
      adaptiveLayout.showListOnTablet();
    }
    if (value === 'settings') {
      setSettingsSection(null);
    }
  }, [adaptiveLayout]);

  const handleSelectSettingsSection = useCallback((section: SettingsSection) => {
    setSettingsSection(section);
    adaptiveLayout.showStageOnTablet();
  }, [adaptiveLayout]);

  const handleBackToSettingsList = useCallback(() => {
    setSettingsSection(null);
    adaptiveLayout.showListOnTablet();
  }, [adaptiveLayout]);

  useEffect(() => {
    const sessionKey = pendingInitialHistorySessionKey;
    if (!sessionKey || initialHistoryLoadingSessionRef.current === sessionKey) {
      return;
    }

    const session = sessionsByKey[sessionKey];
    if (!session) {
      return;
    }

    initialHistoryLoadingSessionRef.current = sessionKey;
    void loadHistory(sessionKey, 'initial').finally(() => {
      initialHistoryLoadingSessionRef.current = null;
      setPendingInitialHistorySessionKey((current) => (current === sessionKey ? null : current));
    });
  }, [loadHistory, pendingInitialHistorySessionKey, sessionsByKey]);

  const openDirectSession = useCallback((contactId: number) => {
    const key = sessionKeyOf('single', contactId);
    setSessionsByKey((prev) => {
      if (prev[key]) {
        return prev;
      }
      return {
        ...prev,
        [key]: createSession('single', contactId, contactsRef.current, groupsRef.current),
      };
    });
    openSession(key);
  }, [openSession]);

  const handleRedialHistoryEntry = useCallback((entry: CallHistoryEntry) => {
    const peer = entry.context.peer;
    if (!peer || entry.scope !== 'direct') return;
    const sessionKey = sessionKeyOf('single', peer.userId);
    openDirectSession(peer.userId);
    handleStartCall(entry.mediaKind, {
      userId: peer.userId,
      name: peer.displayName || contactsRef.current[peer.userId]?.name || `用户 #${peer.userId}`,
      sessionKey,
    });
  }, [handleStartCall, openDirectSession]);

  const openConversationDraft = useCallback((contact: ContactDirectoryUser) => {
    setConversationDraftTarget(contact);
    setConversationDraft(createConversationDraft());
  }, []);

  const openSearchConversationDraft = useCallback((contact: UserSearchResult) => {
    openConversationDraft({
      id: contact.id,
      name: contact.name || `用户 #${contact.id}`,
      email: contact.email,
      phone: '',
      avatarUrl: contact.avatarUrl,
      signInfo: contact.status || '平台用户',
      status: 0,
    });
  }, [openConversationDraft]);

  const handleStartMapConversation = useCallback(async (authorUserId: number) => {
    if (!authorUserId || authState.user?.id === authorUserId) return;
    const knownContact = contactsRef.current[authorUserId];
    if (knownContact) {
      openDirectSession(authorUserId);
      return;
    }
    try {
      const contact = await fetchUserById(authorUserId, apiBase);
      contactsRef.current = { ...contactsRef.current, [authorUserId]: contact };
      setContactsById((prev) => ({ ...prev, [authorUserId]: contact }));
      openDirectSession(authorUserId);
    } catch (error) {
      pushLog(`无法打开聊天: ${getErrorMessage(error)}`, 'error');
    }
  }, [apiBase, authState.user?.id, openDirectSession, pushLog]);

  const handleViewAuthorProfile = useCallback(async (nameOrId: string | number) => {
    if (!nameOrId) return;
    if (loadingProfileUser) {
      pushLog('正在获取其他用户的资料中，请稍候...', 'info');
      return;
    }

    const isId = typeof nameOrId === 'number';

    // 1. Check if it's the current user themselves
    if (authState.user && (isId ? authState.user.id === nameOrId : authState.user.displayName === nameOrId)) {
      const selfContact = contactsRef.current[authState.user.id];
      setViewingProfileUser({
        id: authState.user.id,
        name: authState.user.displayName || '',
        email: authState.user.email || '',
        phone: '',
        avatarUrl: selfContact?.avatarUrl || '',
        signInfo: profileSnapshot.signature.trim() || selfContact?.signInfo || '我自己',
        status: 0,
      });
      setProfileRelationIsFriend(false);
      setProfileRelationIsPending(false);
      setIsProfileViewOpen(true);
      return;
    }

    // 2. Check if the user is already in our friends/contacts list
    const friend = isId
      ? contactsRef.current[nameOrId]
      : Object.values(contactsRef.current).find((c) => c.name === nameOrId);

    if (friend) {
      setViewingProfileUser(friend);
      setProfileRelationIsFriend(true);
      setProfileRelationIsPending(false);
      setIsProfileViewOpen(true);
      return;
    }

    // 3. Search and fetch info for the stranger or system user
    setLoadingProfileUser(true);
    if (isId) {
      pushLog(`正在获取用户 ID #${nameOrId} 的详细资料...`, 'info');
      try {
        const fullUser = await fetchUserById(nameOrId, apiBase);
        setViewingProfileUser(fullUser);
        setProfileRelationIsFriend(false);
        const pending = friendRequests.some((r) => r.fromUserId === nameOrId && r.status === 0);
        setProfileRelationIsPending(pending);
        setIsProfileViewOpen(true);
      } catch (err) {
        pushLog(`获取资料失败: ${getErrorMessage(err)}`, 'error');
      } finally {
        setLoadingProfileUser(false);
      }
    } else {
      pushLog(`正在查找用户 ${nameOrId} 的详细资料...`, 'info');
      try {
        const results = await searchUsers(nameOrId, apiBase);
        const matched = results.find((r) => r.name === nameOrId);
        if (matched) {
          const fullUser = await fetchUserById(matched.id, apiBase);
          setViewingProfileUser(fullUser);
          setProfileRelationIsFriend(false);
          const pending = friendRequests.some((r) => r.fromUserId === matched.id && r.status === 0);
          setProfileRelationIsPending(pending);
          setIsProfileViewOpen(true);
        } else {
          // Fallback: If we couldn't find the exact name in DB, show a temporary/placeholder profile modal!
          // This avoids the silent "no response" feeling for system-generated/seeded accounts (like Locs.Club Guide)
          setViewingProfileUser({
            id: 0,
            name: nameOrId,
            email: '',
            phone: '',
            avatarUrl: '',
            signInfo: '系统内置账户或未注册成员',
            status: 0,
          });
          setProfileRelationIsFriend(false);
          setProfileRelationIsPending(false);
          setIsProfileViewOpen(true);
          pushLog(`未在系统用户库中找到用户 ${nameOrId} 的精确注册信息，展示临时占位主页。`, 'warn');
        }
      } catch (err) {
        pushLog(`获取资料失败: ${getErrorMessage(err)}`, 'error');
      } finally {
        setLoadingProfileUser(false);
      }
    }
  }, [apiBase, authState.user, profileSnapshot.signature, pushLog, friendRequests, loadingProfileUser]);

  const closeConversationDraft = useCallback(() => {
    setConversationDraftTarget(null);
    setConversationDraft(createConversationDraft());
  }, []);

  const submitConversationDraft = useCallback(async () => {
    const target = conversationDraftTarget;
    if (!target) {
      return;
    }
    const key = sessionKeyOf('single', target.id);
    const existingSession = sessionsRef.current[key];
    const session = existingSession ?? createSession('single', target.id, {
      ...contactsRef.current,
      [target.id]: target,
    }, groupsRef.current);
    const text = composeConversationMessage(target.name, conversationDraft);

    setConversationDraftSubmitting(true);
    setContactsById((prev) => ({ ...prev, [target.id]: prev[target.id] ?? target }));
    sessionsRef.current = {
      ...sessionsRef.current,
      [key]: session,
    };
    setSessionsByKey((prev) => ({
      ...prev,
      [key]: prev[key] ?? session,
    }));
    setActiveTab('chats');
    setActiveSessionKey(key);
    activeSessionKeyRef.current = key;
    ImManager.getInstance().setCurrentSessionKey(key);
    setPendingInitialHistorySessionKey(key);

    try {
      await handleSendMessage(text);
      closeConversationDraft();
    } finally {
      setConversationDraftSubmitting(false);
    }
  }, [closeConversationDraft, conversationDraft, conversationDraftTarget, handleSendMessage]);

  const openGroupSession = useCallback((groupId: number) => {
    const key = sessionKeyOf('group', groupId);
    setSessionsByKey((prev) => {
      if (prev[key]) {
        return prev;
      }
      return {
        ...prev,
        [key]: createSession('group', groupId, contactsRef.current, groupsRef.current),
      };
    });
    openSession(key);
  }, [openSession]);

  const {
    createGroupDraft,
    setCreateGroupDraft,
    createGroupSubmitting,
    groupDetailLoading,
    groupDetailError,
    setGroupDetailError,
    resetGroupUiState,
    handleToggleCreateGroupMember,
    refreshGroupDetail,
    handleCreateGroup,
  } = useWorkspaceGroups({
    connState,
    pushLog,
    imRef,
    imUserIdRef,
    contactsRef,
    groupsRef,
    setGroupsById,
    setSessionsByKey,
    setIsCreateGroupModalOpen,
    openGroupSession,
    sessionKeyOf,
    resolveSessionPresentation,
  });

  const resetWorkspace = useCallback(() => {
    identityCoordinatorRef.current = null;
    identityCoordinatorOwnerRef.current = null;
    identityHydrationPromiseRef.current = null;
    identityStoreRef.current.clear();
    setContactsById({});
    setGroupsById({});
    setSessionsByKey({});
    setActiveSessionKey(null);
    setComposerText('');
    setDirectoryFilter('');
    setFriendRequestNote('');
    setFriendRequestTarget(null);
    setIsFriendRequestModalOpen(false);
    resetDirectoryState();
    setIsCreateGroupModalOpen(false);
    setIsGroupDetailModalOpen(false);
    setIsGroupMemberModalOpen(false);
    setGroupMemberEditMode('add');
    setGroupMemberDraftIds([]);
    setGroupMemberSubmitting(false);
    resetGroupUiState();
    resetFriendRequestsState();
    resetProfileState();
  }, [resetDirectoryState, resetFriendRequestsState, resetGroupUiState, resetProfileState]);

  const handleLogout = useCallback(() => {
    setIsLogoutModalOpen(true);
  }, []);

  const handleExecuteLogout = useCallback(async () => {
    setIsLoggingOut(true);
    disconnectIm('已断开实时连接');
    resetWorkspace();
    pushLog('正在退出登录…', 'info');
    try {
      await logout();
      clearCredentials();
      setPassword('');
      pushLog('已安全退出登录', 'success');
    } catch (error) {
      pushLog(`退出失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setIsLoggingOut(false);
      setIsLogoutModalOpen(false);
    }
  }, [disconnectIm, logout, pushLog, resetWorkspace]);

  const handleComposerKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleOpenGroupDetail = useCallback(() => {
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    if (!session || session.sessionType !== 'group') {
      return;
    }
    setIsGroupDetailModalOpen(true);
    void refreshGroupDetail(session.sessionId).catch(() => {
      /* handled via state */
    });
  }, [refreshGroupDetail]);

  const handleOpenGroupMemberModal = useCallback((mode: GroupMemberEditMode) => {
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    if (!session || session.sessionType !== 'group') {
      return;
    }
    setGroupMemberEditMode(mode);
    setGroupMemberDraftIds([]);
    setGroupDetailError(null);
    setIsGroupMemberModalOpen(true);
  }, []);

  const handleToggleGroupMemberDraft = useCallback((memberId: number) => {
    setGroupMemberDraftIds((prev) => (
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    ));
  }, []);

  const handleSubmitGroupMemberChange = useCallback(async () => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;
    const memberIds = [...new Set(groupMemberDraftIds.filter((id) => id > 0))];

    if (!client || connState !== 'online' || userId == null) {
      pushLog('IM 未在线，无法修改群成员', 'warn');
      return;
    }
    if (!session || session.sessionType !== 'group') {
      pushLog('请先打开一个群组', 'warn');
      return;
    }
    if (memberIds.length === 0) {
      pushLog(groupMemberEditMode === 'add' ? '请先选择要添加的成员' : '请先选择要移出的成员', 'warn');
      return;
    }

    setGroupMemberSubmitting(true);
    setGroupDetailError(null);
    try {
      const proto = getProto();
      await client.changeGroupMembers(
        userId,
        session.sessionId,
        memberIds,
        groupMemberEditMode === 'add'
          ? proto.IM.BaseDefine.GroupModifyType.GROUP_MODIFY_TYPE_ADD
          : proto.IM.BaseDefine.GroupModifyType.GROUP_MODIFY_TYPE_DEL,
      );
      await refreshGroupDetail(session.sessionId);
      setIsGroupMemberModalOpen(false);
      setGroupMemberDraftIds([]);
      pushLog(
        groupMemberEditMode === 'add'
          ? `已向 ${session.title} 添加 ${memberIds.length} 位成员`
          : `已从 ${session.title} 移出 ${memberIds.length} 位成员`,
        'success',
      );
    } catch (error) {
      const message = getErrorMessage(error);
      setGroupDetailError(message);
      pushLog(`${groupMemberEditMode === 'add' ? '添加成员' : '移出成员'}失败: ${message}`, 'error');
    } finally {
      setGroupMemberSubmitting(false);
    }
  }, [connState, groupMemberDraftIds, groupMemberEditMode, pushLog, refreshGroupDetail]);

  useEffect(() => {
    if (authState.user?.email) {
      setEmail(authState.user.email);
    }
  }, [authState.user?.email]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 700px), (max-width: 960px) and (max-height: 520px)');
    const syncActiveTabForViewport = () => {
      if (!mediaQuery.matches) {
        setIsUserMenuOpen(false);
      }
    };

    syncActiveTabForViewport();
    mediaQuery.addEventListener('change', syncActiveTabForViewport);
    return () => {
      mediaQuery.removeEventListener('change', syncActiveTabForViewport);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isUserMenuOpen) return;
      const menu = userMenuRef.current;
      const menuBtn = userMenuBtnRef.current;
      if (!menu || !menuBtn) return;
      const target = event.target as Node;
      if (!menu.contains(target) && !menuBtn.contains(target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Auto-connect IM when authenticated
  useEffect(() => {
    if (authState.status !== 'authenticated' || !authState.user) {
      disconnectIm();
      chatRepository.close();
      resetWorkspace();
      localChatSnapshotUserIdRef.current = null;
      return;
    }

    if (localChatSnapshotUserIdRef.current !== authState.user.id) {
      void hydrateLocalWorkspaceState(authState.user.id).catch((error) => {
        pushLog(`读取本地聊天缓存失败: ${getErrorMessage(error)}`, 'warn');
      });
    }

    // Load friend requests
    void loadFriendRequests();
    void refreshPendingCount();

    // Auto-connect IM if not already connected
    if (connState === 'idle' && imRef.current == null) {
      void establishImSession(authState.user).catch(() => {
        pushLog('聊天服务暂时不可用，请稍后重试', 'warn');
      });
    }
  }, [authState.status, authState.user, connState, disconnectIm, establishImSession, hydrateLocalWorkspaceState, loadFriendRequests, pushLog, refreshPendingCount, resetWorkspace]);

  useEffect(() => {
    if (bootstrappingHintHideTimerRef.current != null) {
      window.clearTimeout(bootstrappingHintHideTimerRef.current);
      bootstrappingHintHideTimerRef.current = null;
    }

    if (!isBootstrapping) {
      const shownAt = bootstrappingHintShownAtRef.current;
      if (shownAt == null) {
        setShowBootstrappingHint(false);
        return;
      }

      const elapsed = Date.now() - shownAt;
      const remaining = Math.max(BOOTSTRAPPING_HINT_MIN_VISIBLE_MS - elapsed, 0);
      if (remaining === 0) {
        bootstrappingHintShownAtRef.current = null;
        setShowBootstrappingHint(false);
        return;
      }

      bootstrappingHintHideTimerRef.current = window.setTimeout(() => {
        bootstrappingHintShownAtRef.current = null;
        bootstrappingHintHideTimerRef.current = null;
        setShowBootstrappingHint(false);
      }, remaining);
      return () => {
        if (bootstrappingHintHideTimerRef.current != null) {
          window.clearTimeout(bootstrappingHintHideTimerRef.current);
          bootstrappingHintHideTimerRef.current = null;
        }
      };
    }

    const timeoutId = window.setTimeout(() => {
      bootstrappingHintShownAtRef.current = Date.now();
      setShowBootstrappingHint(true);
    }, BOOTSTRAPPING_HINT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isBootstrapping]);

  useEffect(() => {
    if (!isPostModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePostComposer();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSubmitPost) {
        event.preventDefault();
        void handleSubmitPost();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canSubmitPost, closePostComposer, handleSubmitPost, isPostModalOpen]);

  // Auto-reconnect when connection drops
  useEffect(() => {
    if (connState !== 'error' || !authState.user || authState.status !== 'authenticated') {
      return;
    }

    // Wait 3 seconds before attempting to reconnect
    const timeoutId = window.setTimeout(() => {
      pushLog('尝试自动重新连接聊天服务...', 'info');
      void establishImSession(authState.user!).catch(() => {
        pushLog('自动重连失败，将在稍后重试', 'warn');
      });
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [connState, authState.user, authState.status, establishImSession, pushLog]);

  const sessions = useMemo(
    () => {
      const selfUserId = imUserId ?? authState.user?.id ?? 0;
      return Object.values(presentedSessionsByKey)
        .filter((session) => !(session.sessionType === 'single' && session.sessionId === selfUserId))
        .sort((left, right) => right.updatedAt - left.updatedAt);
    },
    [authState.user?.id, imUserId, presentedSessionsByKey],
  );

  const visibleSessions = useMemo(() => {
    const filter = directoryFilter.trim().toLowerCase();
    if (!filter) {
      return sessions;
    }
    return sessions.filter((session) => (
      session.title.toLowerCase().includes(filter)
      || session.subtitle.toLowerCase().includes(filter)
      || session.lastMsgPreview.toLowerCase().includes(filter)
    ));
  }, [directoryFilter, sessions]);

  const contacts = useMemo(() => {
    const selfUserId = imUserId ?? authState.user?.id ?? 0;
    return Object.values(contactsById)
      .filter((contact) => contact.id !== selfUserId)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [authState.user?.id, contactsById, imUserId]);

  const visibleContacts = useMemo(() => {
    const filter = directoryFilter.trim().toLowerCase();
    if (!filter) {
      return contacts;
    }
    return contacts.filter((contact) => (
      contact.name.toLowerCase().includes(filter)
      || contact.email.toLowerCase().includes(filter)
      || contact.signInfo.toLowerCase().includes(filter)
    ));
  }, [contacts, directoryFilter]);

  const groups = useMemo(
    () => Object.values(groupsById).sort((left, right) => left.name.localeCompare(right.name)),
    [groupsById],
  );

  const visibleGroups = useMemo(() => {
    const filter = directoryFilter.trim().toLowerCase();
    if (!filter) {
      return groups;
    }
    return groups.filter((group) => (
      group.name.toLowerCase().includes(filter)
      || String(group.id).includes(filter)
    ));
  }, [directoryFilter, groups]);

  const activeSession = activeSessionKey ? presentedSessionsByKey[activeSessionKey] ?? null : null;
  useEffect(() => {
    setRealtimeFileNotice(null);
    realtimeFileStartingSessionKeyRef.current = null;
    setRealtimeFileStartingSessionKey(null);
  }, [activeSessionKey]);

  const fileOperationCapability = evaluateFileOperationCapability({
    imConnected: connState === 'online',
    sessionKnown: activeSession != null,
    singleSession: activeSession?.sessionType === 'single',
    sessionCurrent: activeSession?.sessionKey === activeSessionKey,
    realtimeRuntimeReady: fileTransferManagerRef.current != null,
    peerPresence: activeSession?.sessionType === 'single'
      ? presenceByUserId[activeSession.sessionId]
      : undefined,
  });
  const activeGroup = activeSession?.sessionType === 'group'
    ? groupsById[activeSession.sessionId]
    : undefined;
  const activeMessages = activeSession?.messages ?? [];
  useEffect(() => {
    const transferIds = Array.from(new Set(activeMessages
      .map((message) => message.fileMeta?.transport === 'online' ? message.fileMeta.transferId : undefined)
      .filter((transferId): transferId is string => Boolean(transferId))));
    const missingIds = transferIds.filter((transferId) => !Object.prototype.hasOwnProperty.call(fileTransferLifecycleById, transferId));
    if (missingIds.length === 0) return;
    void chatRepository.getFileTransferLifecycles(missingIds)
      .catch((): Record<string, FileTransferLifecycleSnapshot> => ({}))
      .then(async (cachedById) => {
        if (Object.keys(cachedById).length > 0) {
          setFileTransferLifecycleById((current) => {
            const next = { ...current };
            for (const [transferId, cached] of Object.entries(cachedById)) {
              const existing = current[transferId];
              if (!existing || existing.version < cached.version) next[transferId] = cached;
            }
            return next;
          });
        }
        const uncachedIds = missingIds.filter((transferId) => !cachedById[transferId]);
        if (uncachedIds.length === 0) return;
        try {
          const projections = await requestFileTransferProjections(apiBase, uncachedIds);
          await Promise.all(projections.map((snapshot) => chatRepository.saveFileTransferLifecycle(snapshot)));
          setFileTransferLifecycleById((current) => {
            const next = { ...current };
            const returned = new Set(projections.map((snapshot) => snapshot.transferId));
            for (const transferId of uncachedIds) {
              if (!returned.has(transferId) && !next[transferId]) next[transferId] = false;
            }
            for (const snapshot of projections) {
              const existing = next[snapshot.transferId];
              if (!existing || existing.version < snapshot.version) next[snapshot.transferId] = snapshot;
            }
            return next;
          });
        } catch {
          // Keep the locally cached state and render an unavailable card only
          // when no projection exists; never expose transport/loading errors.
        }
      });
  }, [activeMessages, apiBase]);

  const syncFileTransferLifecycle = useCallback(async () => {
    if (!currentUserId) return;
    if (fileTransferSyncInFlightRef.current) {
      fileTransferSyncPendingRef.current = true;
      return;
    }
    fileTransferSyncInFlightRef.current = true;
    try {
      do {
        fileTransferSyncPendingRef.current = false;
        const cursor = await chatRepository.getFileTransferSyncCursor(currentUserId);
        const result = await requestFileTransferSync(apiBase, cursor);
        if (result.resetRequired) {
          // A clean-break deployment may report a retained-floor reset. The
          // history page will repopulate only the IDs it actually displays.
          return;
        }
        await chatRepository.applyFileTransferSync(currentUserId, result.changes, result.nextCursor);
        if (result.changes.length > 0) {
          setFileTransferLifecycleById((current) => {
            const next = { ...current };
            for (const change of result.changes) next[change.projection.transferId] = change.projection;
            return next;
          });
        }
      } while (fileTransferSyncPendingRef.current);
    } catch {
      // Sync is a background reconciliation path; retain the last known UI.
    } finally {
      fileTransferSyncInFlightRef.current = false;
    }
  }, [apiBase, currentUserId]);

  useEffect(() => {
    if (!currentUserId) return undefined;
    void syncFileTransferLifecycle();
    const timer = window.setInterval(() => void syncFileTransferLifecycle(), 10000);
    return () => window.clearInterval(timer);
  }, [currentUserId, syncFileTransferLifecycle]);

  const terminalFileTransferSyncSignal = useMemo(
    () => fileTransferSyncSignal(fileTransferTasks),
    [fileTransferTasks],
  );
  useEffect(() => {
    if (terminalFileTransferSyncSignal) void syncFileTransferLifecycle();
  }, [syncFileTransferLifecycle, terminalFileTransferSyncSignal]);

  const realtimeTargetIds = useMemo(() => {
    if (!activeSession || !authState.user) {
      return [];
    }
    if (activeSession.sessionType === 'single') {
      return [activeSession.sessionId];
    }
    const group = groupsById[activeSession.sessionId];
    return (group?.memberIds ?? []).filter((id) => id !== authState.user?.id);
  }, [activeSession, authState.user, groupsById]);

  const handleStartRealtimeVideoRoom = useCallback(() => {
    if (!activeSession || !authState.user) {
      return;
    }
    if (activeSession.sessionType !== 'group') {
      handleStartCall('video');
      return;
    }
    void startRealtimeSession({
      kind: 'group-video',
      conversation: {
        type: activeSession.sessionType,
        id: activeSession.sessionId,
        title: activeSession.title,
      },
      targetUserIds: realtimeTargetIds,
    }).catch((error) => {
      console.error('[realtime] failed to start video room', error);
      pushLog('视频房间启动失败，请稍后重试', 'error');
    });
  }, [activeSession, authState.user, handleStartCall, pushLog, realtimeTargetIds, startRealtimeSession]);

  const handleStartRealtimeAudioRoom = useCallback(() => {
    if (!activeSession || !authState.user) {
      return;
    }
    if (activeSession.sessionType !== 'group') {
      handleStartCall('audio');
      return;
    }
    void startRealtimeSession({
      kind: 'group-audio',
      conversation: {
        type: activeSession.sessionType,
        id: activeSession.sessionId,
        title: activeSession.title,
      },
      targetUserIds: realtimeTargetIds,
    }).catch((error) => {
      console.error('[realtime] failed to start audio room', error);
      pushLog('语音房间启动失败，请稍后重试', 'error');
    });
  }, [activeSession, authState.user, handleStartCall, pushLog, realtimeTargetIds, startRealtimeSession]);

  const handleStartRealtimeWhiteboard = useCallback(() => {
    if (!activeSession || !authState.user) {
      return;
    }
    void startRealtimeSession({
      kind: activeSession.sessionType === 'group' ? 'group-whiteboard' : 'direct-whiteboard',
      conversation: {
        type: activeSession.sessionType,
        id: activeSession.sessionId,
        title: activeSession.title,
      },
      targetUserIds: realtimeTargetIds,
    }).catch((error) => {
      console.error('[realtime] failed to start whiteboard', error);
      pushLog('共享画板启动失败，请稍后重试', 'error');
    });
  }, [activeSession, authState.user, pushLog, realtimeTargetIds, startRealtimeSession]);

  const resolveRealtimeUserName = useCallback((userId: number) => (
    userId === authState.user?.id
      ? (authState.user?.displayName || authState.user?.email || '我')
      : contactsById[userId]?.name || `#${userId}`
  ), [authState.user, contactsById]);
  const isAwaitingInitialHistory =
    !!activeSession
    && pendingInitialHistorySessionKey === activeSession.sessionKey
    && activeMessages.length === 0;

  const groupAddCandidates = useMemo(() => {
    if (!activeGroup) {
      return [];
    }
    return contacts.filter((contact) => !activeGroup.memberIds.includes(contact.id));
  }, [activeGroup, contacts]);
  const groupRemoveCandidates = useMemo(() => {
    if (!activeGroup) {
      return [];
    }
    return activeGroup.memberIds
      .filter((memberId) => memberId !== currentUserId)
      .map((memberId) => contactsById[memberId] ?? {
        id: memberId,
        name: `成员 #${memberId}`,
        email: '',
        phone: '',
        avatarUrl: '',
        signInfo: '',
        status: 0,
      });
  }, [activeGroup, contactsById, currentUserId]);
  const isOnline = connState === 'online';
  const totalUnreadCount = sessions.reduce((sum, session) => sum + session.unreadCount, 0);
  useEffect(() => {
    if (activeSessionKey && !sessionsByKey[activeSessionKey]) {
      setActiveSessionKey(sessions[0]?.sessionKey ?? null);
    }
  }, [activeSessionKey, sessions, sessionsByKey]);

  useEffect(() => {
    if (!activeSession || activeSession.sessionType !== 'group') {
      setIsGroupDetailModalOpen(false);
      setIsGroupMemberModalOpen(false);
      setGroupMemberDraftIds([]);
      setGroupDetailError(null);
    }
  }, [activeSession]);

  if (authState.status === 'checking') {
    return <Center h="100dvh" bg="var(--mantine-color-body)">
      <Stack align="center" role="status">
        {authState.error ? <>
          <Text>{authState.error}</Text>
          <Button onClick={() => void refreshSession().catch(() => {})}>重新连接</Button>
        </> : <><Loader size="sm" /><Text size="sm">正在恢复登录状态…</Text></>}
      </Stack>
    </Center>;
  }

  if (authState.status !== 'authenticated' || !authState.user) {
    return (
      <MantineAuthScreen
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        authStatus={authState.status}
        authError={authState.error}
        onLogin={handleLogin}
        onRegister={register}
        onResendRegistration={resendRegistration}
        onCompleteRegistration={finishRegistration}
        onRequestPasswordReset={requestPasswordReset}
        onSubmitPasswordReset={resetPassword}
        registrationPolicy={registrationPolicy}
        oauthOptions={oauthOptions}
      />
    );
  }

  const currentUser = authState.user;
  const effectiveSettingsSection = settingsSection
    ?? (adaptiveLayout.viewportMode === 'desktop' ? 'profile' : null);

  const shouldShowConversation = activeTab === 'chats';
  const hubsStage = (
    <HubsStage
      apiBase={apiBase}
      cdnBase={cdnBase}
      onStartConversation={handleStartMapConversation}
      onViewProfile={(authorUserId) => void handleViewAuthorProfile(authorUserId)}
      postsStage={(viewSwitcher) => (
        <PlazaStage
          apiBase={apiBase}
          cdnBase={cdnBase}
          plazaArticles={plazaArticles}
          plazaHasMore={plazaHasMore}
          plazaLoadState={plazaLoadState}
          plazaError={plazaError}
          setIsPlazaAtTop={setIsPlazaAtTop}
          plazaRefreshSource={plazaRefreshSource}
          isPostModalOpen={isPostModalOpen}
          setIsPostModalOpen={setIsPostModalOpen}
          postTitle={postTitle}
          setPostTitle={setPostTitle}
          postContent={postContent}
          setPostContent={setPostContent}
          postTags={postTags}
          setPostTags={setPostTags}
          postSubmitting={postSubmitting}
          postSubmitStatus={postSubmitStatus}
          postSubmitError={postSubmitError}
          postImages={postImages}
          postVideo={postVideo}
          postLocation={postLocation}
          postLocationPending={postLocationPending}
          canSubmitPost={canSubmitPost}
          closePostComposer={closePostComposer}
          loadPlazaArticles={loadPlazaArticles}
          handleSubmitPost={handleSubmitPost}
          handleAddPostImages={handleAddPostImages}
          handleRemovePostImage={handleRemovePostImage}
          handleSelectPostVideo={handleSelectPostVideo}
          handleRemovePostVideo={handleRemovePostVideo}
          handleUseCurrentLocation={handleUseCurrentLocation}
          removePostLocation={removePostLocation}
          handleToggleArticleLike={handleToggleArticleLike}
          handleToggleArticleFavorite={handleToggleArticleFavorite}
          setPreviewImageUrl={setPreviewImageUrl}
          onViewAuthorProfile={handleViewAuthorProfile}
          viewSwitcher={viewSwitcher}
        />
      )}
    />
  );
  const workspaceStage = activeTab === 'settings' ? (
    <SettingsStage
      section={effectiveSettingsSection}
      viewportMode={adaptiveLayout.viewportMode}
      currentUser={currentUser}
      profileDraft={profileDraft}
      profileLoadState={profileLoadState}
      profileError={profileError}
      profileDirty={profileDirty}
      profileUploadingField={profileUploadingField}
      fallbackAvatarUrl={contactsById[currentUser.id]?.avatarUrl}
      cdnBase={cdnBase}
      onBack={handleBackToSettingsList}
      onProfileChange={handleProfileDraftChange}
      onProfileImagePick={handleProfileImagePick}
      onSaveProfile={() => void handleSaveProfile()}
      onResetProfile={handleResetProfileDraft}
      apiBase={apiBase}
      onRedial={handleRedialHistoryEntry}
    />
  ) : activeTab === 'plaza' && adaptiveLayout.viewportMode !== 'mobile' ? (
    hubsStage
  ) : shouldShowConversation ? (
    <ConversationStage
      activeSession={activeSession}
      activeGroup={activeGroup}
      activeMessages={activeMessages}
      currentUserId={currentUser.id}
      cdnBase={cdnBase}
      contactsById={contactsById}
      callBootstrap={callBootstrap}
      callConnectionState={callConnectionState}
      activeCall={activeCall}
      isOnline={isOnline}
      connState={connState}
      composerText={composerText}
      setComposerText={setComposerText}
      isAudioMode={isAudioMode}
      setIsAudioMode={setIsAudioMode}
      isRecordingAudio={isRecordingAudio}
      recordingLabel={recordingLabel}
      recordingLevels={recordingLevels}
      composerRef={composerRef}
      feedEnd={feedEnd}
      emojiToggleRef={emojiToggleRef}
      isEmojiPickerOpen={isEmojiPickerOpen}
      setIsEmojiPickerOpen={setIsEmojiPickerOpen}
      loadHistory={loadHistory}
      isAwaitingInitialHistory={isAwaitingInitialHistory}
      handleStartAudioRecording={handleStartAudioRecording}
      handleStopAudioRecording={handleStopAudioRecording}
      handleOpenGroupDetail={handleOpenGroupDetail}
      handleStartCall={handleStartCall}
      realtimeConnectionState={realtimeConnectionState}
      activeRealtimeSession={activeRealtimeSession}
      handleStartRealtimeAudioRoom={handleStartRealtimeAudioRoom}
      handleStartRealtimeVideoRoom={handleStartRealtimeVideoRoom}
      handleStartRealtimeWhiteboard={handleStartRealtimeWhiteboard}
      handleComposerKeyDown={handleComposerKeyDown}
      handleChatImageSelect={handleChatImageSelect}
      handleChatFileSelect={handleChatFileSelect}
      handleRealtimeFileSelect={handleRealtimeFileSelect}
      fileOperationCapability={fileOperationCapability}
      realtimeFileStarting={realtimeFileStartingSessionKey === activeSession?.sessionKey}
      realtimeFileNotice={realtimeFileNotice}
      handleAcceptFileTask={handleAcceptFileTask}
      handleAcceptRealtimeFile={handleAcceptRealtimeFile}
      activeFileTransfers={fileTransferTasks.filter((task) => task.sessionKey === activeSessionKey)}
      fileTransferLifecycleById={fileTransferLifecycleById}
      handleSendMessage={handleSendMessage}
      handleBlockUser={handleBlockUser}
      blockingUserId={blockingUserId}
      apiBase={apiBase}
      onBackToList={handleBackToList}
      groupMemberSummary={groupMemberSummary}
      setPreviewImageUrl={setPreviewImageUrl}
      onViewUserProfile={handleViewAuthorProfile}
    />
  ) : (
    <Center h="100%" px="md" bg={{ base: 'var(--mantine-color-body)', md: 'transparent' }}>
      <Text c="dimmed" size="sm" ta="center">
        选择一位联系人，聊点什么吧
      </Text>
    </Center>
  );
  const mobileNavigationItems = getWorkspaceNavigationItems({
    totalUnreadCount,
    pendingRequestCount,
    iconSize: 16,
  });
  return (
    <>
      <Box
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 'auto',
          bottom: adaptiveLayout.viewportMode === 'mobile'
            ? 'calc(76px + env(safe-area-inset-bottom, 0px))'
            : 'max(16px, env(safe-area-inset-bottom, 0px))',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 250,
          pointerEvents: 'none',
          opacity: showBootstrappingHint && !imError ? 1 : 0,
          visibility: showBootstrappingHint && !imError ? 'visible' : 'hidden',
          transform: showBootstrappingHint && !imError ? 'translateY(0)' : 'translateY(4px)',
          transition: 'opacity 200ms ease, transform 200ms ease, visibility 200ms',
        }}
      >
        <Group
          gap={5}
          wrap="nowrap"
          style={{
            width: 'fit-content',
            minHeight: 26,
            marginLeft: 'auto',
            marginRight: 'max(16px, env(safe-area-inset-right, 0px))',
            padding: '3px 8px',
            borderRadius: 8,
            color: 'var(--locs-text-muted)',
            backgroundColor: 'var(--locs-panel)',
            border: '1px solid var(--locs-border)',
          }}
        >
          <Loader size={11} color="terracotta" />
          <Text size="11px" fw={700} c="var(--locs-text-muted)" style={{ letterSpacing: '0.2px' }}>
            同步中
          </Text>
        </Group>
      </Box>
      <AdaptiveWorkspaceShell
        viewportMode={adaptiveLayout.viewportMode}
        tabletPane={adaptiveLayout.tabletPane}
        desktopListCollapsed={adaptiveLayout.desktopListCollapsed}
        effectiveListCollapsed={adaptiveLayout.effectiveListCollapsed}
        isMobileStageVisible={
          adaptiveLayout.isMobileStageVisible
          || (adaptiveLayout.viewportMode === 'mobile' && activeTab === 'settings' && settingsSection !== null)
        }
        onToggleDesktopListCollapsed={adaptiveLayout.toggleDesktopListCollapsed}
        plainStage={activeTab === 'settings'}
        navRail={(
          <WorkspaceNavRail
            currentUser={currentUser}
            cdnBase={cdnBase}
            ownerContact={contactsById[currentUser.id]}
            ownerAvatarUrl={profileSnapshot.avatar?.url}
            profileSignature={profileSnapshot.signature}
            isOnline={isOnline}
            connState={connState}
            activeTab={activeTab}
            totalUnreadCount={totalUnreadCount}
            pendingRequestCount={pendingRequestCount}
            isUserMenuOpen={isUserMenuOpen}
            userMenuRef={userMenuRef}
            userMenuBtnRef={userMenuBtnRef}
            onNavigate={handleWorkspaceNavigate}
            setIsUserMenuOpen={setIsUserMenuOpen}
            handleLogout={handleLogout}
          />
        )}
        listPanel={(
          <WorkspaceListPanel
            currentUser={currentUser}
            cdnBase={cdnBase}
            profileAvatarUrl={profileSnapshot.avatar?.url}
            profileSignature={profileSnapshot.signature}
            isOnline={isOnline}
            connState={connState}
            imError={imError}
            activeTab={activeTab}
            contactsSubView={contactsSubView}
            directoryFilter={directoryFilter}
            totalUnreadCount={totalUnreadCount}
            visibleSessions={visibleSessions}
            activeSessionKey={activeSessionKey}
            visibleContacts={visibleContacts}
            groups={groups}
            visibleGroups={visibleGroups}
            contactsById={contactsById}
            presenceByUserId={presenceByUserId}
            pendingRequestCount={pendingRequestCount}
            searchKeyword={searchKeyword}
            searchState={searchState}
            searchError={searchError}
            searchResults={searchResults}
            sendingRequestByUserId={sendingRequestByUserId}
            groupSearchKeyword={groupSearchKeyword}
            groupSearchResults={groupSearchResults}
            groupSearchState={groupSearchState}
            groupSearchError={groupSearchError}
            friendRequests={friendRequests}
            friendRequestsLoading={friendRequestsLoading}
            friendRequestActionById={friendRequestActionById}
            isUserMenuOpen={isUserMenuOpen}
            userMenuRef={userMenuRef}
            userMenuBtnRef={userMenuBtnRef}
            searchInputRef={searchInputRef}
            setIsUserMenuOpen={setIsUserMenuOpen}
            setActiveTab={setActiveTab}
            setContactsSubView={setContactsSubView}
            setDirectoryFilter={setDirectoryFilter}
            setIsPostModalOpen={setIsPostModalOpen}
            setSearchKeyword={setSearchKeyword}
            setSearchResults={setSearchResults}
            setSearchState={setSearchState}
            setSearchError={setSearchError}
            setFriendRequestTarget={setFriendRequestTarget}
            setFriendRequestNote={setFriendRequestNote}
            setIsFriendRequestModalOpen={setIsFriendRequestModalOpen}
            setGroupSearchKeyword={setGroupSearchKeyword}
            setGroupSearchResults={setGroupSearchResults}
            setGroupSearchState={setGroupSearchState}
            setGroupSearchError={setGroupSearchError}
            setIsCreateGroupModalOpen={setIsCreateGroupModalOpen}
            onNavigate={handleWorkspaceNavigate}
            handleLogout={handleLogout}
            handleReconnectIm={handleReconnectIm}
            openSession={openSession}
            openDirectSession={openDirectSession}
            openConversationDraft={openConversationDraft}
            openSearchConversationDraft={openSearchConversationDraft}
            openGroupSession={openGroupSession}
            handleSearchUsers={handleSearchUsers}
            handleSearchGroups={handleSearchGroups}
            handleApproveRequest={handleApproveRequest}
            handleRejectRequest={handleRejectRequest}
            loadFriendRequests={loadFriendRequests}
            mobilePlazaContent={activeTab === 'plaza' && adaptiveLayout.viewportMode !== 'desktop' ? hubsStage : undefined}
            groupMemberSummary={groupMemberSummary}
            groupsById={groupsById}
            onViewUserProfile={handleViewAuthorProfile}
            selectedSettingsSection={effectiveSettingsSection}
            onSelectSettingsSection={handleSelectSettingsSection}
          />
        )}
        stage={workspaceStage}
        mobileNav={(
          <MobileBottomTabBar
            activeTab={activeTab}
            tabs={mobileNavigationItems}
            onChange={handleWorkspaceNavigate}
          />
        )}
      />

      <ImageLightbox
        opened={!!previewImageUrl}
        onClose={() => setPreviewImageUrl(null)}
        imageUrl={previewImageUrl}
      />

      {pendingImageDraft ? (
        <ImagePreviewModal
          opened
          onClose={clearPendingImageDraft}
          imageUrl={pendingImageDraft.previewUrl}
          confirmFile={pendingImageDraft.file}
          onConfirm={() => void handleConfirmChatImageSend()}
        />
      ) : null}

      <GroupManagementModals
        activeGroup={activeGroup}
        currentUser={currentUser}
        contactsById={contactsById}
        presenceByUserId={presenceByUserId}
        isGroupDetailModalOpen={isGroupDetailModalOpen}
        isGroupMemberModalOpen={isGroupMemberModalOpen}
        groupMemberSubmitting={groupMemberSubmitting}
        groupDetailLoading={groupDetailLoading}
        groupDetailError={groupDetailError}
        groupAddCandidates={groupAddCandidates}
        groupRemoveCandidates={groupRemoveCandidates}
        groupMemberEditMode={groupMemberEditMode}
        groupMemberDraftIds={groupMemberDraftIds}
        setIsGroupDetailModalOpen={setIsGroupDetailModalOpen}
        setIsGroupMemberModalOpen={setIsGroupMemberModalOpen}
        refreshGroupDetail={refreshGroupDetail}
        handleOpenGroupMemberModal={handleOpenGroupMemberModal}
        handleToggleGroupMemberDraft={handleToggleGroupMemberDraft}
        handleSubmitGroupMemberChange={handleSubmitGroupMemberChange}
      />

      <WorkspaceOverlays
        incomingCall={incomingCall}
        activeCall={activeCall}
        isMuted={isMuted}
        isVideoEnabled={isVideoEnabled}
        callAudioLevels={callAudioLevels}
        callUiState={callUiState}
        callConnectionState={callConnectionState}
        remoteCallMediaRef={remoteCallMediaRef}
        localCallMediaRef={localCallMediaRef}
        remoteCallStream={callRemoteStream}
        localCallStream={callLocalStream}
        handleRespondIncomingCall={handleRespondIncomingCall}
        handleHangupCall={handleHangupCall}
        handleToggleMute={handleToggleMute}
        handleToggleVideo={handleToggleVideo}
        openSession={openSession}
        callBootstrapError={callBootstrapError}
        isCreateGroupModalOpen={isCreateGroupModalOpen}
        setIsCreateGroupModalOpen={setIsCreateGroupModalOpen}
        createGroupSubmitting={createGroupSubmitting}
        createGroupDraft={createGroupDraft}
        setCreateGroupDraft={setCreateGroupDraft}
        contacts={contacts}
        handleToggleCreateGroupMember={handleToggleCreateGroupMember}
        handleCreateGroup={handleCreateGroup}
        isFriendRequestModalOpen={isFriendRequestModalOpen}
        setIsFriendRequestModalOpen={setIsFriendRequestModalOpen}
        friendRequestTarget={friendRequestTarget}
        friendRequestNote={friendRequestNote}
        setFriendRequestNote={setFriendRequestNote}
        handleSendFriendRequest={handleSendFriendRequest}
        sendingRequestByUserId={sendingRequestByUserId}
        conversationDraftTarget={conversationDraftTarget}
        conversationDraft={conversationDraft}
        setConversationDraft={setConversationDraft}
        closeConversationDraft={closeConversationDraft}
        submitConversationDraft={submitConversationDraft}
        conversationDraftSubmitting={conversationDraftSubmitting}
      />

      <RealtimeCollaborationOverlays
        incomingSession={incomingRealtimeSession}
        activeSession={activeRealtimeSession}
        localStream={realtimeLocalStream}
        remoteStreams={realtimeRemoteStreams}
        remoteBoardPatch={remoteBoardPatch}
        remoteBoardSnapshot={remoteBoardSnapshot}
        currentUserName={currentUser.displayName || currentUser.email || '我'}
        resolveUserName={resolveRealtimeUserName}
        acceptIncoming={acceptIncomingRealtimeSession}
        rejectIncoming={rejectIncomingRealtimeSession}
        endSession={endRealtimeSession}
        sendBoardPatch={sendBoardPatch}
      />

      <UserProfileModal
        opened={isProfileViewOpen}
        onClose={() => setIsProfileViewOpen(false)}
        user={effectiveViewingProfileUser}
        homepageBackground={viewingProfileUser?.id === currentUser.id ? profileSnapshot.homepageBackground : null}
        currentUser={currentUser}
        isFriend={profileRelationIsFriend}
        isPending={profileRelationIsPending}
        plazaArticles={plazaArticles}
        cdnBase={cdnBase}
        apiBase={apiBase}
        onStartConversationDraft={openConversationDraft}
        onAddFriend={(u) => {
          setFriendRequestTarget({
            id: u.id,
            name: u.name,
            email: u.email,
            avatarUrl: u.avatarUrl,
            status: '',
          });
          setFriendRequestNote('');
          setIsFriendRequestModalOpen(true);
        }}
        onOpenDirectChat={(u) => openDirectSession(u.id)}
        setPreviewImageUrl={setPreviewImageUrl}
        onPickHomepageBackground={(file) => handleProfileImagePick('homepageBackground', file)}
      />

      <LogoutConfirmModal
        opened={isLogoutModalOpen}
        onClose={() => !isLoggingOut && setIsLogoutModalOpen(false)}
        onConfirm={() => void handleExecuteLogout()}
        currentUser={currentUser}
        avatarUrl={contactsById[currentUser.id]?.avatarUrl}
        loading={isLoggingOut}
      />
    </>
  );
}
